import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'

const useMounted = (): boolean => {
    const [mounted, setMounted] = useState(false)

    useEffect(() => {
        setMounted(true)
    }, [])

    return mounted
}

const useOverlayBehaviour = (open: boolean, onClose: () => void) => {
    const panelRef = useRef<HTMLDivElement>(null)
    const closeRef = useRef(onClose)

    useEffect(() => {
        closeRef.current = onClose
    })

    useEffect(() => {
        if (!open) return

        const previous = document.activeElement as HTMLElement | null
        const { overflow } = document.body.style

        document.body.style.overflow = 'hidden'

        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') closeRef.current()

            if (event.key === 'Tab' && panelRef.current) {
                const focusable = panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')

                if (focusable.length === 0) return

                const first = focusable[0]
                const last = focusable[focusable.length - 1]

                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault()
                    last.focus()
                }
                else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault()
                    first.focus()
                }
            }
        }

        document.addEventListener('keydown', onKey)

        const timer = window.setTimeout(() => {
            const target = panelRef.current?.querySelector<HTMLElement>('[data-autofocus]') ?? panelRef.current

            target?.focus()
        }, 30)

        return () => {
            document.body.style.overflow = overflow
            document.removeEventListener('keydown', onKey)
            window.clearTimeout(timer)
            previous?.focus?.()
        }
    }, [open])

    return panelRef
}

interface DrawerProps {
    open: boolean
    onClose: () => void
    title: ReactNode
    side?: 'start' | 'end'
    className?: string
    footer?: ReactNode
    children: ReactNode
}

export const Drawer: FC<DrawerProps> = ({ open, onClose, title, side = 'end', className, footer, children }) => {
    const mounted = useMounted()
    const panelRef = useOverlayBehaviour(open, onClose)
    const { tx } = useI18n()

    if (!mounted || !open) return null

    return createPortal(
        <div className='fixed inset-0 z-[70]'>
            <div className='absolute inset-0 bg-navy-950/40 backdrop-blur-[2px] animate-in fade-in duration-200' onClick={onClose} aria-hidden='true' />

            <div
                ref={panelRef}
                role='dialog'
                aria-modal='true'
                aria-label={typeof title === 'string' ? title : undefined}
                tabIndex={-1}
                className={cn(
                    'absolute top-0 bottom-0 flex w-full max-w-md flex-col bg-white shadow-2xl outline-none animate-in duration-300 ease-out',
                    side === 'end' ? 'end-0 slide-in-from-end' : 'start-0 slide-in-from-start',
                    className
                )}
            >
                <div className='flex items-center justify-between gap-4 border-b border-border px-5 py-4'>
                    <div className='text-lg font-semibold text-navy-950'>{title}</div>
                    <button type='button' onClick={onClose} className='inline-flex size-9 items-center justify-center rounded-full text-navy-600 transition-colors hover:bg-navy-50 hover:text-navy-950 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-400/30' aria-label={tx('إغلاق', 'Close')}>
                        <X className='size-5' />
                    </button>
                </div>

                <div className='flex-1 overflow-y-auto overscroll-contain'>{children}</div>

                {footer && <div className='border-t border-border bg-white px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]'>{footer}</div>}
            </div>
        </div>,
        document.body
    )
}

interface ModalProps {
    open: boolean
    onClose: () => void
    title: ReactNode
    description?: ReactNode
    className?: string
    footer?: ReactNode
    children?: ReactNode
}

export const Modal: FC<ModalProps> = ({ open, onClose, title, description, className, footer, children }) => {
    const mounted = useMounted()
    const panelRef = useOverlayBehaviour(open, onClose)
    const { tx } = useI18n()

    if (!mounted || !open) return null

    return createPortal(
        <div className='fixed inset-0 z-[80] flex items-end justify-center p-0 sm:items-center sm:p-6'>
            <div className='absolute inset-0 bg-navy-950/45 backdrop-blur-[2px] animate-in fade-in duration-200' onClick={onClose} aria-hidden='true' />

            <div
                ref={panelRef}
                role='dialog'
                aria-modal='true'
                aria-label={typeof title === 'string' ? title : undefined}
                tabIndex={-1}
                className={cn(
                    'relative flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-3xl bg-white shadow-2xl outline-none animate-in fade-in slide-in-from-bottom-6 duration-300 sm:rounded-3xl sm:zoom-in-95 sm:slide-in-from-bottom-2',
                    className
                )}
            >
                <div className='flex items-start justify-between gap-4 px-6 pt-6'>
                    <div className='flex flex-col gap-1'>
                        <h2 className='text-lg font-semibold text-navy-950'>{title}</h2>
                        {description && <p className='text-sm leading-relaxed text-muted-foreground'>{description}</p>}
                    </div>

                    <button type='button' onClick={onClose} className='-me-2 -mt-1 inline-flex size-9 shrink-0 items-center justify-center rounded-full text-navy-600 transition-colors hover:bg-navy-50 hover:text-navy-950 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-400/30' aria-label={tx('إغلاق', 'Close')}>
                        <X className='size-5' />
                    </button>
                </div>

                {children && <div className='overflow-y-auto px-6 pt-5 pb-6'>{children}</div>}

                {footer && <div className='flex flex-col-reverse gap-2 border-t border-border px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end'>{footer}</div>}
            </div>
        </div>,
        document.body
    )
}
